import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import type { Squad } from '../../../../types';
import { db } from '../../../../shared/storage/firebase';
import ManageSquadUsersModal from '../../../admin/components/ManageSquadUsersModal';

type Props = {
  squad: Squad | null;
  onSquadUpdate?: (updatedSquad: Squad) => void;
};

function safeString(v: any): string {
  return String(v || '').trim();
}

export default function StudentRosterLogistic(props: Props): React.ReactElement {
  const { squad, onSquadUpdate } = props;
  const [open, setOpen] = useState(false);
  const studentIds = useMemo(() => {
    const ids = Array.isArray((squad as any)?.studentIds) ? ((squad as any).studentIds as any[]) : [];
    return ids.map((x) => safeString(x)).filter(Boolean);
  }, [squad]);

  const [idToName, setIdToName] = useState<Record<string, string>>({});

  const fetchKey = useMemo(() => studentIds.join('|'), [studentIds]);
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (studentIds.length === 0) {
        if (alive) setIdToName({});
        return;
      }

      const MAX_PREVIEW = 12;
      const ids = studentIds.slice(0, MAX_PREVIEW);
      const pairs = await Promise.all(
        ids.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'users', id));
            if (snap.exists()) {
              const data: any = snap.data() || {};
              const name = safeString(data.displayName) || safeString(data.email) || id;
              return [id, name] as const;
            }
          } catch {
            // ignore (best-effort)
          }
          return [id, id] as const;
        })
      );

      if (!alive) return;
      const next: Record<string, string> = {};
      for (const [id, name] of pairs) next[id] = name;
      setIdToName(next);
    };
    void run();
    return () => {
      alive = false;
    };
  }, [fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 800, fontSize: 13 }}>
          Linked student users: <span style={{ fontWeight: 900 }}>{studentIds.length}</span>
        </div>
        <button type="button" className="mini-btn" onClick={() => setOpen(true)}>
          Manage roster…
        </button>
      </div>

      {studentIds.length === 0 ? (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          No student users linked yet.
        </div>
      ) : (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {studentIds.slice(0, 12).map((id) => (
            <span
              key={id}
              title={id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                borderRadius: 999,
                border: '1px solid #e2e8f0',
                background: '#f8fafc',
                color: '#0f172a',
                fontSize: 12,
                fontWeight: 800,
                maxWidth: 260,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {idToName[id] || id}
            </span>
          ))}
          {studentIds.length > 12 ? (
            <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              +{studentIds.length - 12} more
            </span>
          ) : null}
        </div>
      )}

      <ManageSquadUsersModal
        isOpen={open}
        onClose={() => setOpen(false)}
        squad={squad}
        onSquadUpdate={(updated) => onSquadUpdate?.(updated)}
      />
    </div>
  );
}

